import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./HrDashboard.css";
import { getStaffStats, patchDepartmentHeadcountPlans } from "../../../api/hrApi";
import Preloader from "../../Preloader/Preloader";
import {
  getDefaultAnchorDate,
  getPeriodRange,
  PERIOD_TYPES,
  shiftAnchorDate,
  canShiftForward,
} from "./periodUtils";
import { getStaffingTotals, mapStaffStatsToDashboard, applyHeadcountPlansUpdate } from "./mapStaffStats";
import PeriodSelector from "./PeriodSelector";
import KpiCards from "./KpiCards";
import StaffingTable from "./StaffingTable";
import TurnoverSection from "./TurnoverSection";
import TenureSection from "./TenureSection";
import GenderSection from "./GenderSection";

function HrDashboard() {
  const [periodType, setPeriodType] = useState(PERIOD_TYPES.YEAR);
  const [anchorDate, setAnchorDate] = useState(getDefaultAnchorDate);
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planSaveError, setPlanSaveError] = useState(null);

  const { dateFrom, dateTo } = useMemo(
    () => getPeriodRange(periodType, anchorDate),
    [periodType, anchorDate],
  );

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getStaffStats({ dateFrom, dateTo });
      setDashboardData(mapStaffStatsToDashboard(response));
    } catch (err) {
      setDashboardData(null);
      setError(err.message || "Не удалось загрузить данные дашборда");
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const staffingTotals = useMemo(() => {
    if (!dashboardData) return null;
    return getStaffingTotals(
      dashboardData.staffingByDepartment,
      dashboardData.summary,
    );
  }, [dashboardData]);

  const canGoForward = useMemo(
    () => canShiftForward(periodType, anchorDate),
    [periodType, anchorDate],
  );

  const handlePlanSave = useCallback(async (departmentId, plan) => {
    setPlanSaveError(null);

    try {
      const updatedDepartments = await patchDepartmentHeadcountPlans([
        { department_id: departmentId, plan },
      ]);
      setDashboardData((current) =>
        current ? applyHeadcountPlansUpdate(current, updatedDepartments) : current,
      );
    } catch (err) {
      setPlanSaveError(err.message || "Не удалось сохранить план");
      throw err;
    }
  }, []);

  if (isLoading && !dashboardData) {
    return <Preloader />;
  }

  return (
    <div className="hr-dashboard">
      <div className="hr-dashboard__header">
        <h1 className="hr-dashboard__title">HR-дашборд</h1>
        <PeriodSelector
          periodType={periodType}
          anchorDate={anchorDate}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onPeriodTypeChange={setPeriodType}
          onPrev={() =>
            setAnchorDate((current) =>
              shiftAnchorDate(periodType, current, -1),
            )
          }
          onNext={() =>
            canGoForward &&
            setAnchorDate((current) => shiftAnchorDate(periodType, current, 1))
          }
          canGoForward={canGoForward}
        />
      </div>

      {error && <div className="hr-dashboard__error">{error}</div>}

      {isLoading && <div className="hr-dashboard__loading">Обновление данных…</div>}

      {dashboardData && staffingTotals && (
        <>
          <KpiCards summary={dashboardData.summary} />
          <StaffingTable
            departments={dashboardData.staffingByDepartment}
            totals={staffingTotals}
            onPlanSave={handlePlanSave}
            planSaveError={planSaveError}
          />
          <TurnoverSection turnover={dashboardData.turnover} />
          <TenureSection
            highlights={dashboardData.tenure.highlights}
            categories={dashboardData.tenure.categories}
            departments={dashboardData.tenure.departments}
          />
          <GenderSection summary={dashboardData.gender} />
        </>
      )}
    </div>
  );
}

export default HrDashboard;
